import {
  Inject,
  Injectable,
}                      from '@angular/core';
import { Store }       from '@ngrx/store';
import {
  combineLatest,
  map,
}                      from 'rxjs';
import {
  Beneficiary,
  canCreatePaymentFromAccount,
  DuplicatePaymentRequest,
  payDocumentType,
  PaymentData,
  RbDocument,
}                      from '~domain/types';
import { ENVIRONMENT } from '../../app.constants';
import {
  addBeneficiary,
  addPayment,
  assignDocument,
  checkDuplicatePayments,
  clearDuplicatePayments,
  clearProcessDocument,
  deleteBeneficiary,
  fetchClientBeneficiaries,
  fetchDocumentProcessingData,
  reloadPage,
  updateBeneficiary,
}                      from '../../state/app.actions';
import {
  selectAssignDocumentClients,
  selectAssignDocumentIsLoading,
  selectBeneficiaryEditing,
  selectClientBeneficiaries,
  selectClientBeneficiariesLoading,
  selectDuplicatePaymentsLatestDate,
  selectDuplicatePaymentsType,
  selectFetchingDocumentProcessingData,
  selectGlobalBeneficiaries,
  selectGlobalBeneficiariesLoading,
  selectOffice,
  selectPaymentAccounts,
  selectPaymentClients,
  selectPaymentSaving,
  selectProcessDocumentType,
  selectProcessDocumentUrl,
}                      from '../../state/app.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProcessDocumentService {
  fetchingDocumentProcessingData$ = this.store.select(selectFetchingDocumentProcessingData);
  processDocumentType$ = this.store.select(selectProcessDocumentType);
  processDocumentUrl$ = this.store.select(selectProcessDocumentUrl);
  paymentAccounts$ = combineLatest([
    this.store.select(selectPaymentAccounts),
    this.store.select(selectOffice),
  ]).pipe(
    map(([paymentAccounts, office]) => office
      ? paymentAccounts.filter(account => canCreatePaymentFromAccount(account, office, this.environment.production))
      : []),
  );
  paymentClients$ = this.store.select(selectPaymentClients);
  globalBeneficiaries$ = this.store.select(selectGlobalBeneficiaries);
  clientBeneficiaries$ = this.store.select(selectClientBeneficiaries);
  globalBeneficiariesLoading$ = this.store.select(selectGlobalBeneficiariesLoading);
  clientBeneficiariesLoading$ = this.store.select(selectClientBeneficiariesLoading);
  paymentBeneficiaryEditing$ = this.store.select(selectBeneficiaryEditing);
  paymentSaving$ = this.store.select(selectPaymentSaving);
  duplicatePaymentsType$ = this.store.select(selectDuplicatePaymentsType);
  duplicatePaymentsLatestDate$ = this.store.select(selectDuplicatePaymentsLatestDate);
  assignDocumentClients$ = this.store.select(selectAssignDocumentClients);
  assignDocumentIsLoading$ = this.store.select(selectAssignDocumentIsLoading);

  constructor(
    @Inject(ENVIRONMENT)
    private readonly environment: Environment,
    private readonly store: Store,
  ) {
  }

  fetchNextDocumentProcessingData(document: RbDocument, queuedDocuments: RbDocument[]) {
    this.store.dispatch(fetchDocumentProcessingData({
      document,
      documentType: payDocumentType(document),
      queuedDocuments,
    }));
  }

  savePayment(paymentData: PaymentData, queuedDocuments: RbDocument[]) {
    this.store.dispatch(addPayment({
      paymentData,
      anotherPayment: false,
      queuedDocuments,
      reload: false,
      context: 'process',
    }));
  }

  fetchClientBeneficiaries(clientId: string) {
    this.store.dispatch(fetchClientBeneficiaries({ clientId }));
  }

  checkDuplicatePayments(clientId: string, request: DuplicatePaymentRequest) {
    this.store.dispatch(checkDuplicatePayments({ clientId, request }));
  }

  clearDuplicatePayments() {
    this.store.dispatch(clearDuplicatePayments());
  }

  updateBeneficiary(beneficiaryId: string, clientId: string, beneficiary: Beneficiary) {
    this.store.dispatch(updateBeneficiary({ beneficiaryId, clientId, beneficiary }));
  }

  addBeneficiary(clientId: string, beneficiary: Beneficiary) {
    this.store.dispatch(addBeneficiary({ clientId, beneficiary }));
  }

  deleteBeneficiary(beneficiaryId: string, clientId: string) {
    this.store.dispatch(deleteBeneficiary({ beneficiaryId, clientId }));
  }

  assignDocument(documentId: string, clientId: string, queuedDocuments: RbDocument[]) {
    this.store.dispatch(assignDocument({ documentIds: [documentId], clientId, queuedDocuments }));
  }

  clearProcessDocument() {
    this.store.dispatch(clearProcessDocument());
  }

  reloadPage() {
    this.store.dispatch(reloadPage());
  }
}
